/**
 * Hand-drawn SVG arrow path generator.
 * Produces cubic bezier paths with controlled wobble for a sketched look.
 */

// Seeded PRNG (mulberry32) for stable wobble per step
function mulberry32(seed) {
  return function () {
    seed |= 0
    seed = (seed + 0x6d2b79f5) | 0
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/**
 * Get the best connection points between a label rect and a target rect.
 * Returns { from: {x,y}, to: {x,y} } picking the closest edges.
 * @param {number} gap - px to inset arrow endpoints away from each rect edge
 */
export function getConnectionPoints(labelRect, targetRect, gap = 16) {
  const labelCenter = {
    x: labelRect.x + labelRect.width / 2,
    y: labelRect.y + labelRect.height / 2,
  }
  const targetCenter = {
    x: targetRect.x + targetRect.width / 2,
    y: targetRect.y + targetRect.height / 2,
  }

  // Candidate points on label edges, inset by `gap`
  const labelPoints = [
    { x: labelCenter.x, y: labelRect.y - gap, side: 'top' },
    { x: labelCenter.x, y: labelRect.y + labelRect.height + gap, side: 'bottom' },
    { x: labelRect.x - gap, y: labelCenter.y, side: 'left' },
    { x: labelRect.x + labelRect.width + gap, y: labelCenter.y, side: 'right' },
  ]

  // Candidate points on target edges, inset by `gap`
  const targetPoints = [
    { x: targetCenter.x, y: targetRect.y - gap, side: 'top' },
    { x: targetCenter.x, y: targetRect.y + targetRect.height + gap, side: 'bottom' },
    { x: targetRect.x - gap, y: targetCenter.y, side: 'left' },
    { x: targetRect.x + targetRect.width + gap, y: targetCenter.y, side: 'right' },
  ]

  let bestDist = Infinity
  let from = labelPoints[0]
  let to = targetPoints[0]

  for (const lp of labelPoints) {
    for (const tp of targetPoints) {
      const dx = lp.x - tp.x
      const dy = lp.y - tp.y
      const dist = dx * dx + dy * dy
      if (dist < bestDist) {
        bestDist = dist
        from = lp
        to = tp
      }
    }
  }

  return { from, to }
}

/**
 * Generate a hand-drawn arrow SVG path string.
 * @param {object} from - { x, y } start point
 * @param {object} to   - { x, y } end point
 * @param {number} seed - integer seed for stable randomness
 * @returns {{ path: string, headPath: string, length: number }}
 */
export function generateArrowPath(from, to, seed = 0) {
  const rng = mulberry32(seed)

  const dx = to.x - from.x
  const dy = to.y - from.y
  const dist = Math.sqrt(dx * dx + dy * dy)

  // Perpendicular direction
  const px = -dy / (dist || 1)
  const py = dx / (dist || 1)

  // Single control point = single arc, never S-curves
  // Position varies along the line and always has some perpendicular offset
  const tPos = 0.3 + rng() * 0.4 // 0.30 – 0.70
  const wobbleAmp = Math.min(dist * 0.18, 45) + 10
  const rawWobble = (rng() - 0.5) * 2 * wobbleAmp
  // Ensure minimum curve of 15px
  const sign = rawWobble >= 0 ? 1 : -1
  const wobble = Math.abs(rawWobble) < 15 ? sign * 15 : rawWobble

  const cp = {
    x: from.x + dx * tPos + px * wobble,
    y: from.y + dy * tPos + py * wobble,
  }

  // Arrival angle from curve tangent (cp → to)
  const curveAngle = Math.atan2(to.y - cp.y, to.x - cp.x)
  // Straight-line angle (from → to)
  const straightAngle = Math.atan2(dy, dx)
  // Blend: 60% curve direction, 40% straight — keeps arrowhead sensible
  // but still follows the curve
  let angleDiff = curveAngle - straightAngle
  // Normalize to [-PI, PI]
  while (angleDiff > Math.PI) angleDiff -= 2 * Math.PI
  while (angleDiff < -Math.PI) angleDiff += 2 * Math.PI
  // Clamp deviation to ±25 degrees
  const maxDev = 0.44 // ~25 degrees
  angleDiff = Math.max(-maxDev, Math.min(maxDev, angleDiff))
  const angle = straightAngle + angleDiff

  // Arrowhead prongs — minimal tilt variation
  const headLen = 12
  const headAngle = 0.45
  const tiltJitter = (rng() - 0.5) * 0.04 // ±0.02 rad shared tilt, very subtle
  const h1 = {
    x: to.x - headLen * Math.cos(angle - headAngle + tiltJitter),
    y: to.y - headLen * Math.sin(angle - headAngle + tiltJitter),
  }
  const h2 = {
    x: to.x - headLen * Math.cos(angle + headAngle + tiltJitter),
    y: to.y - headLen * Math.sin(angle + headAngle + tiltJitter),
  }

  // Line goes all the way to the tip
  const path = `M ${from.x} ${from.y} Q ${cp.x} ${cp.y}, ${to.x} ${to.y}`
  const headPath = `M ${h1.x} ${h1.y} L ${to.x} ${to.y} L ${h2.x} ${h2.y}`

  const length = approximateQuadLength(from, cp, to)

  return { path, headPath, length }
}

function approximateQuadLength(p0, p1, p2, segments = 20) {
  let length = 0
  let prev = p0
  for (let i = 1; i <= segments; i++) {
    const t = i / segments
    const it = 1 - t
    const pt = {
      x: it * it * p0.x + 2 * it * t * p1.x + t * t * p2.x,
      y: it * it * p0.y + 2 * it * t * p1.y + t * t * p2.y,
    }
    const pdx = pt.x - prev.x
    const pdy = pt.y - prev.y
    length += Math.sqrt(pdx * pdx + pdy * pdy)
    prev = pt
  }
  return length
}
